/**
 * Shared utilities for matching (row/list helpers, no JSX).
 */

import {
  DEFAULT_MATCHING_SESSION_FILTERS,
  PAIR_RECURRENCE_LABELS,
  type MatchingSessionColumnFilters,
} from '@/components/pairs/pair-configs';
import type { AssignmentSource } from '@/types/inperson/pair.types';
import type {
  ParticipantInput,
  StudentTutorPairInput,
  SessionForMatching,
} from '@/types/inperson/matching.types';
import type { UserWithProfile } from '@/types/user.types';
import type { SessionListItem } from '@/types/inperson/session.types';
import type { SlotConflictSession } from '@/types/slot-availability.types';
import { formatPairDate, formatPairTimeRange } from '@/components/pairs/inperson/pair-table-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { resolveListDateRange } from '@/utils/list-date-range-utils';

/** One student–tutor assignment on a matching session. */
export interface MatchingStudentTutorMatch {
  studentId: string;
  tutorId: string;
  assignmentSource?: AssignmentSource;
}

/** Header chips derived from a session (date, time, site, recurrence). */
export interface MatchingSessionHeaderDisplay {
  dateLabel: string;
  timeLabel: string;
  establishmentDisplay: string | null;
  recurrenceLabel: string | null;
}

/** Optional getters used to exclude users already on the expanded session. */
export interface MatchingSessionHandlersLike {
  getTempTutors?: () => UserWithProfile[];
  getTempStudents?: () => UserWithProfile[];
  getSpecificMatches?: () => Array<{ studentId: string; tutorId: string }>;
}

/** Partial session-column date filters produced from calendar picks. */
type MatchingSessionDateFilterPatch = Partial<
  Pick<MatchingSessionColumnFilters, 'dateRange' | 'customDateFrom' | 'customDateTo'>
>;

/** Minimal list-API user shape before {@link transformMatchingUserWithProfile}. */
type MatchingListUser = {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  displayName?: string;
  createdAt?: string;
  updatedAt?: string;
  userProfiles?: Array<{
    subjects?: unknown;
    level?: { id: string; label: string };
  }>;
};

/** Resolved drop target for student-to-tutor or drop-zone drops. */
export type MatchingDropTarget =
  | { kind: 'student-to-tutor-chip'; sessionId: string; tutorId: string }
  | { kind: 'tutor-to-dropzone'; sessionId: string }
  | { kind: 'student-to-dropzone'; sessionId: string };

export {
  formatPairFilterDateToString,
  parsePairFilterDateInput,
  toggleListValue,
} from '@/components/pairs/inperson/pair-table-utils';

/** Known `assignmentSource` values from the in-person matching API. */
const ASSIGNMENT_SOURCES: AssignmentSource[] = [
  'manual',
  'auto_binome_coeur',
  'auto_history',
  'auto_balanced',
];

/** Session participant roles that count as tutors in matching. */
const TUTOR_PARTICIPANT_ROLES = new Set(['tutor', 'volunteer']);

/** Hide `none`; unknown values become spaced words. */
export function formatPairRecurrenceLabel(rec: string | null | undefined): string | null {
  if (!rec || rec === 'none') return null;
  return PAIR_RECURRENCE_LABELS[rec] ?? rec.replaceAll('_', ' ');
}

/** Date / time / establishment / recurrence for the collapse header. */
export function deriveMatchingSessionHeader(
  session: SessionForMatching,
): MatchingSessionHeaderDisplay {
  return {
    dateLabel: formatPairDate(session.sessionDate, 'EEEE d MMMM yyyy', '—'),
    timeLabel: formatPairTimeRange(session.startTime, session.endTime, '--:--', ' – '),
    establishmentDisplay:
      (session.establishmentName ?? session.establishment?.name)?.trim() || null,
    recurrenceLabel: formatPairRecurrenceLabel(session.recurrenceType),
  };
}

/** Split participants into tutors (incl. volunteer) vs students. */
export function splitMatchingSessionParticipants(session: SessionForMatching): {
  tutors: UserWithProfile[];
  students: UserWithProfile[];
} {
  const tutors: UserWithProfile[] = [];
  const students: UserWithProfile[] = [];

  for (const participant of session.participants ?? []) {
    if (!participant.user) continue;
    if (participant.role === 'student') {
      students.push(participant.user);
    } else if (TUTOR_PARTICIPANT_ROLES.has(participant.role)) {
      tutors.push(participant.user);
    }
  }

  return { tutors, students };
}

/** Persistable matches from existing `studentTutorPairs`. */
export function mapSessionPairsToMatches(
  pairs: SessionForMatching['studentTutorPairs'],
): MatchingStudentTutorMatch[] {
  return (pairs ?? []).map((pair) => ({
    studentId: pair.studentUserId,
    tutorId: pair.tutorUserId,
    assignmentSource: pair.assignmentSource,
  }));
}

/** Split participants + map pairs only when those fields are present. */
export function hydrateSessionLocalState(session: SessionForMatching): {
  tutors?: UserWithProfile[];
  students?: UserWithProfile[];
  matches?: MatchingStudentTutorMatch[];
} {
  const next: {
    tutors?: UserWithProfile[];
    students?: UserWithProfile[];
    matches?: MatchingStudentTutorMatch[];
  } = {};
  if (session.participants) {
    const split = splitMatchingSessionParticipants(session);
    next.tutors = split.tutors;
    next.students = split.students;
  }
  if (session.studentTutorPairs) {
    next.matches = mapSessionPairsToMatches(session.studentTutorPairs);
  }
  return next;
}

/** Prefer live counts from the expanded session; else count from participants. */
export function resolveSessionDisplayCounts(
  session: SessionForMatching,
  live?: { tutorCount: number; studentCount: number },
): { tutorCount: number; studentCount: number } {
  if (live) return live;
  const split = splitMatchingSessionParticipants(session);
  return { tutorCount: split.tutors.length, studentCount: split.students.length };
}

/** Activity cap first, then session `maxStudents`. */
export function getMatchingSessionMaxStudents(session: SessionForMatching): number | null {
  return session.activity?.maxStudents ?? session.maxStudents ?? null;
}

/** How many students exceed the cap; 0 when uncapped. */
export function getMatchingStudentsOverCapacity(
  studentCount: number,
  maxStudents: number | null,
): number {
  if (maxStudents === null) return 0;
  return Math.max(0, studentCount - maxStudents);
}

/** Uncapped sessions always accept another participant. */
export function canAddMatchingParticipant(
  currentCount: number,
  max: number | null | undefined,
): boolean {
  if (max == null) return true;
  return currentCount < max;
}

/** Auto-assign needs at least one tutor and one student. */
export function canRunMatchingAutoAssign(tutorCount: number, studentCount: number): boolean {
  return tutorCount > 0 && studentCount > 0;
}

/** Save needs tutors, students, and at least one match. */
export function canSaveMatchingAssignments(
  tutorCount: number,
  studentCount: number,
  matchCount: number,
): boolean {
  return tutorCount > 0 && studentCount > 0 && matchCount > 0;
}

/** Explicit matches win; otherwise cartesian product of all students × tutors. */
export function buildMatchingAssignmentPairs(
  specificMatches: MatchingStudentTutorMatch[],
  students: UserWithProfile[],
  tutors: UserWithProfile[],
): StudentTutorPairInput[] {
  if (specificMatches.length > 0) {
    return specificMatches.map((match) => ({
      studentUserId: match.studentId,
      tutorUserId: match.tutorId,
    }));
  }
  return students.flatMap((student) =>
    tutors.map((tutor) => ({
      studentUserId: student.id,
      tutorUserId: tutor.id,
    })),
  );
}

/** Volunteer role is preserved; everyone else is `tutor` or `student`. */
export function buildMatchingAssignmentParticipants(
  tutors: UserWithProfile[],
  students: UserWithProfile[],
): ParticipantInput[] {
  return [
    ...tutors.map((tutor) => ({
      userId: tutor.id,
      role: (tutor.currentRole === 'volunteer' ? 'volunteer' : 'tutor') as 'tutor' | 'volunteer',
    })),
    ...students.map((student) => ({
      userId: student.id,
      role: 'student' as const,
    })),
  ];
}

/** Auto-assign API pairs → matching-state matches (no `assignmentSource`). */
export function mapAutoAssignPairsToMatches(
  pairs: Array<{ studentUserId: string; tutorUserId: string }>,
): MatchingStudentTutorMatch[] {
  return pairs.map((pair) => ({
    studentId: pair.studentUserId,
    tutorId: pair.tutorUserId,
  }));
}

/** Students currently matched to this tutor, with optional assignment source. */
export function getTutorMatchedStudents(
  tutorId: string,
  specificMatches: MatchingStudentTutorMatch[],
  students: UserWithProfile[],
): Array<UserWithProfile & { assignmentSource?: AssignmentSource }> {
  const matched: Array<UserWithProfile & { assignmentSource?: AssignmentSource }> = [];
  for (const match of specificMatches) {
    if (match.tutorId !== tutorId) continue;
    const student = students.find((item) => item.id === match.studentId);
    if (student) {
      matched.push({ ...student, assignmentSource: match.assignmentSource });
    }
  }
  return matched;
}

/** Compact tutors / students / matchings summary. */
export function formatMatchingParticipantsSummary(
  tutorCount: number,
  studentCount: number,
  matchCount: number,
): string {
  const tutors = `${tutorCount} ${tutorCount > 1 ? 'tuteurs' : 'tuteur'}`;
  const students = `${studentCount} ${studentCount > 1 ? 'élèves' : 'élève'}`;
  const matches = matchCount > 0 ? ` • ${matchCount} matchings` : '';
  return `${tutors} • ${students}${matches}`;
}

/** Custom dates override the preset; otherwise expand today / Mon–Sun week / calendar month. */
export function resolveMatchingSessionDateRange(filters: MatchingSessionColumnFilters): {
  dateFrom?: Date;
  dateTo?: Date;
} {
  return resolveListDateRange(filters, { periodEnd: 'endOfPeriod' });
}

/** List-API params for matching sessions (planned / in-progress only). */
export function buildMatchingSessionsListParams(
  filters: MatchingSessionColumnFilters,
  options: { page: number; pageSize: number; sortOrder: 'asc' | 'desc' },
) {
  const { dateFrom, dateTo } = resolveMatchingSessionDateRange(filters);
  return {
    statuses: ['PLANNED', 'IN_PROGRESS'],
    types: filters.types.length > 0 ? filters.types : undefined,
    establishmentId: filters.establishmentId,
    subjectId: filters.subjectId,
    levelId: filters.levelId,
    search: filters.search,
    sortBy: 'sessionDate',
    sortOrder: options.sortOrder,
    page: options.page,
    pageSize: options.pageSize,
    dateFrom,
    dateTo,
  };
}

/** Default matching filters with the today date preset. */
export function getTodayMatchingSessionFilters(): MatchingSessionColumnFilters {
  return { ...DEFAULT_MATCHING_SESSION_FILTERS, dateRange: 'today' };
}

/** Any type, date, site, subject, level, or search besides the default. */
export function hasActiveMatchingSessionFilters(filters: MatchingSessionColumnFilters): boolean {
  return (
    filters.types.length > 0 ||
    filters.dateRange !== 'all' ||
    !!filters.customDateFrom ||
    !!filters.customDateTo ||
    !!filters.establishmentId ||
    !!filters.subjectId ||
    !!filters.levelId ||
    !!filters.search
  );
}

/** Stable key so calendar sync effects do not fire on same-day `Date` identity. */
export function buildSelectedDatesKey(dates: Date[]): string {
  if (dates.length === 0) return '';
  return dates
    .map((date) => date.getTime())
    .sort((a, b) => a - b)
    .join(',');
}

/** Single calendar pick that is today (used to map back to the today preset). */
export function isSelectedDatesOnlyToday(dates: Date[], now = new Date()): boolean {
  if (dates.length !== 1) return false;
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const selected = dates[0];
  return (
    new Date(selected.getFullYear(), selected.getMonth(), selected.getDate()).getTime() ===
    todayStart
  );
}

/** Inclusive local-day range from the earliest to latest selected date. */
export function selectedDatesToCustomRange(dates: Date[]): { dateFrom: Date; dateTo: Date } {
  const sorted = [...dates].sort((a, b) => a.getTime() - b.getTime());
  const dateFrom = new Date(sorted[0]);
  dateFrom.setHours(0, 0, 0, 0);
  const dateTo = new Date(sorted.at(-1) as Date);
  dateTo.setHours(23, 59, 59, 999);
  return { dateFrom, dateTo };
}

/** Calendar picks → today preset or a custom inclusive range. */
export function matchingSessionFiltersFromSelectedDates(
  dates: Date[],
): MatchingSessionDateFilterPatch {
  if (isSelectedDatesOnlyToday(dates)) {
    return matchingSessionFiltersFromDateRange('today');
  }
  const { dateFrom, dateTo } = selectedDatesToCustomRange(dates);
  return {
    dateRange: 'all',
    customDateFrom: dateFrom,
    customDateTo: dateTo,
  };
}

/** Calendar selection for a preset (`today` → one date; others empty). */
export function calendarDatesForMatchingDateRange(
  range: MatchingSessionColumnFilters['dateRange'],
): Date[] {
  if (range === 'today') return [startOfLocalDay()];
  return [];
}

/** Apply a date-range preset and clear custom from/to. */
export function matchingSessionFiltersFromDateRange(
  range: MatchingSessionColumnFilters['dateRange'],
): MatchingSessionDateFilterPatch {
  return {
    dateRange: range,
    customDateFrom: undefined,
    customDateTo: undefined,
  };
}

/** Custom from/to; any bound switches `dateRange` to `all`. */
export function matchingSessionFiltersFromCustomRange(
  from: Date | undefined,
  to: Date | undefined,
): MatchingSessionDateFilterPatch {
  return {
    customDateFrom: from,
    customDateTo: to,
    ...(from || to ? { dateRange: 'all' as const } : {}),
  };
}

/** Local midnight (calendar filter / today preset). */
export function startOfLocalDay(date = new Date()): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

/** Pluralized session count for the sessions-column header. */
export function formatMatchingSessionCountLabel(count: number): string {
  return `${count} ${count > 1 ? 'séances' : 'séance'}`;
}

/** Page suffix only when there is more than one page. */
export function formatMatchingSessionPageSuffix(page: number, totalPages: number): string {
  return totalPages > 1 ? ` • Page ${page}/${totalPages}` : '';
}

/** Unknown API sources fall back to `manual`. */
function asAssignmentSource(value: string): AssignmentSource {
  return (ASSIGNMENT_SOURCES as readonly string[]).includes(value)
    ? (value as AssignmentSource)
    : 'manual';
}

/** Wrap a single subject into an array; availability is unused in matching. */
export function transformMatchingUserProfile(profile: {
  subjects?: unknown;
  level?: { id: string; label: string };
}) {
  return {
    subjects: profile.subjects ? [profile.subjects] : null,
    availability: null,
    level: profile.level || null,
  };
}

/** Tutors show first+last; students keep API `displayName`. */
export function transformMatchingUserWithProfile(
  user: MatchingListUser,
  role: string,
): UserWithProfile {
  return {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email || null,
    companyId: '',
    firebaseUid: '',
    displayName:
      role === 'student'
        ? user.displayName || null
        : `${user.firstName} ${user.lastName}`.trim() || user.displayName || null,
    photoUrl: null,
    phone: null,
    status: 'active',
    userProfiles: user.userProfiles?.map(transformMatchingUserProfile) || [],
    userRoles: [],
    createdAt: user.createdAt ? new Date(user.createdAt) : new Date(),
    updatedAt: user.updatedAt ? new Date(user.updatedAt) : new Date(),
  };
}

/** Session list item → matching session (typed assignment sources + profiles). */
export function toSessionForMatching(session: SessionListItem): SessionForMatching {
  return {
    ...session,
    participants: session.participants?.map((participant) => ({
      ...participant,
      user: participant.user
        ? transformMatchingUserWithProfile(participant.user, participant.role)
        : undefined,
    })),
    studentTutorPairs: session.studentTutorPairs?.map((pair) => ({
      id: pair.id,
      sessionId: pair.sessionId,
      studentUserId: pair.studentUserId,
      tutorUserId: pair.tutorUserId,
      assignmentSource: asAssignmentSource(pair.assignmentSource),
    })),
  };
}

/** DnD payload: tutor/volunteer vs student (`roleGroup` or `type`). */
export function resolveMatchingDragKind(
  activeData: Record<string, unknown> | undefined,
): { user: UserWithProfile; kind: 'tutor' | 'student' } | null {
  if (!activeData) return null;
  const user = activeData.user as UserWithProfile | undefined;
  if (!user) return null;

  const roleGroup = activeData.roleGroup as string | undefined;
  const type = activeData.type as string | undefined;
  const isTutor =
    roleGroup === 'tutors' ||
    roleGroup === 'volunteers' ||
    type === 'tutor' ||
    type === 'volunteer';
  const isStudent = roleGroup === 'students' || type === 'student';

  if (isTutor) return { user, kind: 'tutor' };
  if (isStudent) return { user, kind: 'student' };
  return null;
}

/** Map over-data + drag kind to a drop target, or null. */
export function resolveMatchingDropTarget(
  activeKind: 'tutor' | 'student' | null,
  overData: Record<string, unknown> | undefined,
): MatchingDropTarget | null {
  if (!overData || !activeKind) return null;
  const sessionId = overData.sessionId as string | undefined;
  if (!sessionId) return null;
  const overType = overData.type as string | undefined;

  if (activeKind === 'student' && overType === 'tutor-chip') {
    const tutorId = overData.tutorId as string | undefined;
    if (!tutorId) return null;
    return { kind: 'student-to-tutor-chip', sessionId, tutorId };
  }
  if (activeKind === 'tutor' && overType === 'matching-dropzone-unified') {
    return { kind: 'tutor-to-dropzone', sessionId };
  }
  if (activeKind === 'student' && overType === 'matching-dropzone-unified') {
    return { kind: 'student-to-dropzone', sessionId };
  }
  return null;
}

/** Previous tutor id when rematching a student to a different tutor. */
export function resolveStudentRematchTutorId(
  studentId: string,
  newTutorId: string,
  matches: Array<{ studentId: string; tutorId: string }>,
): string | null {
  const oldMatch = matches.find((match) => match.studentId === studentId);
  if (oldMatch && oldMatch.tutorId !== newTutorId) return oldMatch.tutorId;
  return null;
}

/** True when `userId` is already in the session participant list. */
export function isUserInList(users: Array<{ id: string }>, userId: string): boolean {
  return users.some((user) => user.id === userId);
}

/** Add the student if missing; rematch only when they already sit on another tutor. */
export function planStudentToTutorChipDrop(params: {
  studentId: string;
  tutorId: string;
  tempStudents: Array<{ id: string }>;
  matches: Array<{ studentId: string; tutorId: string }>;
}): { shouldAddStudent: boolean; unmatchTutorId: string | null } {
  const already = isUserInList(params.tempStudents, params.studentId);
  return {
    shouldAddStudent: !already,
    unmatchTutorId: already
      ? resolveStudentRematchTutorId(params.studentId, params.tutorId, params.matches)
      : null,
  };
}

/** Tutor ids already on the expanded session (hide from the left column). */
export function getExcludedTutorIds(handlers?: MatchingSessionHandlersLike | null): string[] {
  if (!handlers?.getTempTutors) return [];
  return handlers.getTempTutors().map((tutor) => tutor.id);
}

/** Connected volunteer already listed as a tutor on the expanded session. */
export function isVolunteerAlreadyOnExpandedSession(
  isVolunteer: boolean,
  connectedUserId: string | undefined,
  expandedSessionId: string | null,
  excludedTutorIds: string[],
): boolean {
  if (!isVolunteer || !connectedUserId || !expandedSessionId) return false;
  return excludedTutorIds.includes(connectedUserId);
}

/** Leave one slot for the connected volunteer (9 vs 10). */
export function getMatchingTutorsPageSize(
  isVolunteer: boolean,
  isConnectedVolunteerInParticipants: boolean,
): number {
  return isVolunteer && !isConnectedVolunteerInParticipants ? 9 : 10;
}

/** Temp students plus already-matched students (hide from the right column). */
export function getExcludedStudentIds(handlers?: MatchingSessionHandlersLike | null): string[] {
  if (!handlers?.getTempStudents || !handlers?.getSpecificMatches) return [];
  const tempStudents = handlers.getTempStudents();
  const matchedStudentIds = handlers.getSpecificMatches().map((match) => match.studentId);
  return [...tempStudents.map((student) => student.id), ...matchedStudentIds];
}

/** How many students are matched to this tutor on the expanded session. */
export function countTutorMatches(
  handlers: MatchingSessionHandlersLike | undefined,
  tutorId: string,
): number {
  if (!handlers?.getSpecificMatches) return 0;
  return handlers.getSpecificMatches().filter((match) => match.tutorId === tutorId).length;
}

/** Central error copy from {@link getErrorMessage}. */
export function getApiErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** French hint when a slot overlaps an existing session. */
export function formatConflictHint(conflictingSession?: SlotConflictSession): string | undefined {
  if (!conflictingSession) return undefined;
  const start = conflictingSession.startTime?.slice(0, 5) ?? conflictingSession.startTime;
  const end = conflictingSession.endTime?.slice(0, 5) ?? conflictingSession.endTime;
  if (start && end) return getErrorMessage({ code: 'slotConflictRange', start, end });
  if (start) return getErrorMessage({ code: 'slotConflictFrom', start });
  return getErrorMessage('slotConflictGeneric');
}
